import { formatDate } from '@fullcalendar/core';
import { Box, Checkbox, Chip, Link, Tooltip } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BillingClaimPreview,
  BillingMember,
  BillingPagination,
} from '../../../interfaces/billing.rpm';
import { StyledTheme } from '../../../styles/styleTheme';
import { DEFAULT_ROWS_PER_PAGE } from '../../../util/constants';
import { getCapitalizedWord } from '../../../util/stringFormat';
import { CompactTable } from '../../common/CompactTable/CompactTable';
import { Column } from '../../common/CustomTable';
import { getStatusChipColor } from '../common/ChipColors';
import { ClaimLogsModal } from '../common/ClaimLogsModal';
import { CodeChipLevel, CodeChipList } from '../common/CodeChipList';
import { PatientDetailsModal } from '../common/PatientDetailsModal';
import { RpmLoading } from '../common/RpmLoading';
import { formatInsurance } from '../common/Text';
import { isClaimCreatedSuccessfully } from '../helpers';
import RPMNotePreviewModal from '../note';
import { RpmStatus } from '../types/rpms.types';

interface RPMCalculationTableProps {
  onRowSelectionChange: (selectedRows: number[]) => void;
  selectedRows: number[];
  onRowExclusionChange: (excludedRows: { id: number; index: number }[]) => void;
  excludedRows: { id: number; index: number }[];
  isLoading?: boolean;
  data: BillingClaimPreview[];
  pagination: BillingPagination;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  onAllRecordsSelected: (allRecordsSelected: boolean) => void;
  allRecordsSelected: boolean;
}

export const RPMCalculationTable: React.FC<RPMCalculationTableProps> = ({
  isLoading = false,
  onRowSelectionChange,
  selectedRows,
  onRowExclusionChange,
  excludedRows,
  data,
  pagination,
  onPageChange,
  onRowsPerPageChange,
  onAllRecordsSelected,
  allRecordsSelected,
}) => {
  // States
  const [selectedClaimId, setSelectedClaimId] = useState<number | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<BillingMember | null>(null);
  const [isRpmNotePreviewModalOpen, setIsRpmNotePreviewModalOpen] = useState(false);
  const [isPatientDetailModalOpen, setIsPatientDetailModalOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);

  const handleOpenLogs = (claimId: number) => {
    setSelectedClaimId(claimId);
    setIsLogsModalOpen(true);
  };

  const handleCloseLogsModal = () => {
    setIsLogsModalOpen(false);
    setSelectedClaimId(null);
  };

  // Row Selection Handlers
  const handleRowSelection = useCallback(
    (id: number) => {
      const claim = data.find((c) => c.id === id);

      if (!claim?.id) {
        return;
      }

      const updatedSelectedRows = selectedRows.includes(id)
        ? selectedRows.filter((rowId) => rowId !== id)
        : [...selectedRows, id];

      onRowSelectionChange(updatedSelectedRows);

      if (allRecordsSelected) {
        const updatedExcludedRows = selectedRows.includes(id)
          ? [...excludedRows, { id: claim.id, index: id }]
          : excludedRows.filter(({ index }) => index !== id);

        onRowExclusionChange(updatedExcludedRows);
      }
    },
    [
      data,
      onRowSelectionChange,
      selectedRows,
      allRecordsSelected,
      excludedRows,
      onRowExclusionChange,
    ],
  );

  const handleSelectAll = useCallback(() => {
    if (!!selectedRows.length) {
      onRowSelectionChange([]);
    } else {
      onAllRecordsSelected(true);
    }
  }, [onAllRecordsSelected, selectedRows, onRowSelectionChange]);

  const handleDeselectAll = useCallback(() => {
    onAllRecordsSelected(false);
    onRowExclusionChange([]);
  }, [onAllRecordsSelected, onRowExclusionChange]);

  // Pagination Handlers
  const handlePageChange = useCallback(
    (newPage: number) => {
      onPageChange(newPage);
    },
    [onPageChange],
  );

  const handleChangeRowsPerPage = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRowsPerPage = parseInt(event.target.value, 10);
      onRowsPerPageChange(newRowsPerPage);
    },
    [onRowsPerPageChange],
  );

  // Modal Handlers
  const handleOpenRpmNotePreviewModal = useCallback((claimId: number | null) => {
    setSelectedClaimId(claimId);
    setIsRpmNotePreviewModalOpen(true);
  }, []);

  const handleCloseRpmNotePreviewModal = useCallback(() => {
    setSelectedClaimId(null);
    setIsRpmNotePreviewModalOpen(false);
  }, []);

  // Modal Handlers
  const handlePatientDetailOpenModal = useCallback((patient: BillingMember) => {
    setSelectedPatient(patient);
    setIsPatientDetailModalOpen(true);
  }, []);

  const handleClosePatientDetailModal = useCallback(() => {
    setSelectedPatient(null);
    setIsPatientDetailModalOpen(false);
  }, []);

  const columnGroups = useMemo(
    () => [
      {
        id: 'claims',
        title: 'Calculated claims for the period',
        startColumn: 5,
        colspan: 3,
      },
    ],
    [],
  );

  // Columns Configuration
  const columns: Column[] = useMemo(() => {
    const validClaims = data.filter((row) => row.errors.length === 0);
    const validClaimsCount = validClaims.length;
    const validClaimIds = validClaims.map((claim) => claim.id);
    const selectedValidClaimsInPage = selectedRows.filter((id) => validClaimIds.includes(id));

    const someValidClaimsSelected =
      selectedValidClaimsInPage.length > 0 && selectedValidClaimsInPage.length < validClaimsCount;

    return [
      {
        key: 'select',
        label: (
          <Tooltip title='Select/deselect all valid patients across pages' placement='right'>
            <span>
              <Checkbox
                size='small'
                style={{ color: StyledTheme.tealDark }}
                indeterminate={someValidClaimsSelected}
                checked={allRecordsSelected}
                onChange={(event) =>
                  event.target.checked ? handleSelectAll() : handleDeselectAll()
                }
                aria-label='Select Valid Rows'
              />
            </span>
          </Tooltip>
        ),
        align: 'center',
      },
      {
        key: 'id',
        label: 'Claim ID',
      },
      {
        key: 'name',
        label: 'Name',
      },
      {
        key: 'clinic',
        label: 'Clinic',
      },
      {
        key: 'insurance',
        label: 'Insurance',
      },
      {
        key: 'dos',
        label: 'DOS',
      },
      {
        key: 'pos',
        label: 'POS',
      },
      {
        key: 'cptCodes',
        label: 'CPT Codes',
      },
      {
        key: 'note',
        label: 'Note',
        align: 'center',
      },
      {
        key: 'status',
        label: 'Status',
      },
      {
        key: 'issues',
        label: 'Issues',
      },
      {
        key: 'logs',
        label: 'Actions',
        align: 'center',
      },
    ];
  }, [data, handleDeselectAll, handleSelectAll, selectedRows, allRecordsSelected]);

  const tableRows = useMemo(
    () =>
      data.map((row) => ({
        select: (
          <Checkbox
            size='small'
            style={{
              color: row.errors.length === 0 ? StyledTheme.tealDark : StyledTheme.grayLight,
            }}
            disabled={row.errors.length > 0 ? true : false}
            checked={selectedRows.includes(row.id)}
            onChange={() => handleRowSelection(row.id)}
          />
        ),
        id: row.id,
        name: (
          <Link
            component='button'
            variant='body2'
            onClick={() => handlePatientDetailOpenModal(row.billingMember)}>
            {row.memberName}
          </Link>
        ),
        clinic: row.clinicName,
        insurance: formatInsurance(
          row.billingMember?.insuranceCompany,
          row.billingMember?.insuranceCompanyId,
        ),
        dos: isClaimCreatedSuccessfully(row.status) ? formatDate(row.dateOfService) : '',
        pos: isClaimCreatedSuccessfully(row.status) ? row.placeOfService : '',
        status: (
          <Chip
            label={getCapitalizedWord(
              isClaimCreatedSuccessfully(row.status) ? RpmStatus.VALID : RpmStatus.INVALID,
            )}
            sx={getStatusChipColor(
              isClaimCreatedSuccessfully(row.status) ? RpmStatus.VALID : RpmStatus.INVALID,
            )}
            size='small'
          />
        ),
        cptCodes: isClaimCreatedSuccessfully(row.status) ? (
          <CodeChipList codes={row.cptCodes.map((code) => ({ code: code.cptCode }))} />
        ) : (
          ''
        ),
        note: row.noteId ? (
          <Link
            component='button'
            variant='body2'
            onClick={() => handleOpenRpmNotePreviewModal(row.id)}>
            View/Edit Note
          </Link>
        ) : null,
        issues: (
          <CodeChipList
            codes={[
              ...(row.errors || []).map((code) => ({
                code,
                level: 'error' as CodeChipLevel,
              })),
              ...(row.warnings || []).map((code) => ({ code, level: 'warning' as CodeChipLevel })),
            ]}
            allowSlice={false}
          />
        ),
        logs: (
          <Link
            component='button'
            onClick={() => handleOpenLogs(row.id)}
            sx={{ cursor: 'pointer' }}>
            View logs
          </Link>
        ),
      })),
    [
      data,
      handleOpenRpmNotePreviewModal,
      handlePatientDetailOpenModal,
      handleRowSelection,
      selectedRows,
    ],
  );

  useEffect(() => {
    if (allRecordsSelected) {
      const allValidIds = data.filter((claim) => !claim.errors?.length).map(({ id }) => id);
      onRowSelectionChange(allValidIds);
    } else {
      onRowSelectionChange([]);
    }
  }, [allRecordsSelected, data, onRowSelectionChange]);

  return (
    <>
      <Box position='relative'>
        {isLoading && <RpmLoading />}
        <CompactTable
          columns={columns}
          rows={tableRows}
          defaultText='No claims calculations found'
          columnGroups={columnGroups}
          pagination={{
            rowsPerPageOptions: DEFAULT_ROWS_PER_PAGE,
            rowPerPage: pagination.limit,
            page: pagination.currentPage,
            totalCount: pagination.totalCount,
            onPageChange: handlePageChange,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
        />
      </Box>

      <RPMNotePreviewModal
        open={isRpmNotePreviewModalOpen}
        onClose={handleCloseRpmNotePreviewModal}
        claimId={selectedClaimId!}
      />

      <ClaimLogsModal
        open={isLogsModalOpen}
        onClose={handleCloseLogsModal}
        claimId={selectedClaimId}
      />

      <PatientDetailsModal
        open={isPatientDetailModalOpen}
        onClose={handleClosePatientDetailModal}
        patient={selectedPatient}
      />
    </>
  );
};
